import { Country } from '../enums/countries';
import { SupportedLanguages } from '../enums/supported-languages';

/**
 * Maps an ISO 3166 country code (`"DE"`) to an ISO 639 language code (`"de"`).
 *
 * @example
 * mapCountryCodeToLanguageCode('GB') // returns 'en-gb'
 * mapCountryCodeToLanguageCode('XX') // returns 'en' (default)
 * mapCountryCodeToLanguageCode('XX', SupportedLanguages.DE) // returns 'de' (fallback)
 *
 * @param countryCode ISO 3166 country code
 * @param fallbackLanguage Optional fallback language when country code cannot be mapped
 * @returns ISO 639 language code, defaults to 'en'
 */
export const mapCountryCodeToLanguageCode = (
    countryCode: unknown,
    fallbackLanguage?: SupportedLanguages
): SupportedLanguages => {
    // Map of ISO 3166 country codes to ISO 639 language codes
    const countryCodeToLanguageMap = new Map<unknown, SupportedLanguages>([
        [Country.DE, SupportedLanguages.DE],
        [Country.GB, SupportedLanguages.EN_GB],
        [Country.IE, SupportedLanguages.EN_GB],
        [Country.AT, SupportedLanguages.DE],
        [Country.NL, SupportedLanguages.NL],
        [Country.TR, SupportedLanguages.TR],
        [Country.US, SupportedLanguages.EN],
        [Country.NO, SupportedLanguages.NO],
        [Country.SE, SupportedLanguages.SV],
        [Country.DK, SupportedLanguages.DA],
        [Country.FI, SupportedLanguages.FI],
        [Country.EE, SupportedLanguages.EN_GB],
        [Country.LV, SupportedLanguages.LV],
        [Country.LT, SupportedLanguages.LT],
        [Country.BE, SupportedLanguages.EN_GB],
        [Country.LU, SupportedLanguages.LB],
        [Country.CZ, SupportedLanguages.CS],
        [Country.PL, SupportedLanguages.PL]
    ]);

    // If `fallbackLanguage` was provided, get the corresponding value from SupportedLanguages
    const fallback = fallbackLanguage ?? SupportedLanguages.EN;

    // Get country code from map or return fallback
    return countryCodeToLanguageMap.get(countryCode) ?? fallback;
};
